import { Howl, Howler } from 'howler';
import React, { useEffect, useRef, useState } from 'react';
import { SOUNDS } from './logic/assetManifest';

class MusicManager {
  constructor() {
    this.tracks = {};
    this.currentTrack = null;
    this.currentTrackId = null;
    this.fadeTime = 1000;
    this.musicVolume = 0.5;
    this.lastStopPositions = {}; // Track the position when each track was stopped
    this.lastStopTimes = {}; // Track when each track was last stopped
    this.replayThreshold = 30000; // 30 seconds in milliseconds
  }

  addTrack(trackId, config) {
    if (!this.tracks[trackId]) {
      this.tracks[trackId] = new Howl({
        src: [config.src || config.srcs],
        loop: config.loop ?? true,
        volume: 0,
        onend: () => {
          if (this.currentTrackId === trackId) {
            this.tracks[trackId].play();
          }
        },
        onstop: () => {
          this.lastStopTimes[trackId] = Date.now();
          this.lastStopPositions[trackId] = this.tracks[trackId].seek();
        }
      });
    }
  }

  playMusic(trackId, forceRestart = false) {
    if (!this.tracks[trackId]) {
      console.warn(`Music track ${trackId} not found`);
      return;
    }

    const lastStopTime = this.lastStopTimes[trackId] || 0;
    const timeSinceStop = Date.now() - lastStopTime;

    if (this.currentTrackId === trackId && this.tracks[trackId].playing()) {
      return;
    }

    const newTrack = this.tracks[trackId];
    const oldTrack = this.currentTrack;

    newTrack.volume(0);

    // If within replay threshold and not forcing restart, resume from last position
    if (!forceRestart && timeSinceStop < this.replayThreshold && this.lastStopPositions[trackId]) {
      console.log("asdfasdf2")
      newTrack.seek(this.lastStopPositions[trackId]);
    } else {
      // Reset to beginning if forcing restart or outside threshold
      newTrack.seek(0);
    }

    newTrack.play();
    newTrack.fade(0, this.musicVolume, this.fadeTime);

    if (oldTrack && this.currentTrackId !== trackId) {
      console.log("asdfasdf", this.currentTrackId, trackId)
      oldTrack.fade(oldTrack.volume(), 0, this.fadeTime);
      setTimeout(() => {
        oldTrack.stop();
      }, this.fadeTime + 100);
    }

    this.currentTrack = newTrack;
    this.currentTrackId = trackId;
  }

  stopAllMusic() {
    const now = Date.now();
    Object.entries(this.tracks).forEach(([trackId, track]) => {
      if (track.playing()) {
        this.lastStopPositions[trackId] = track.seek();
        this.lastStopTimes[trackId] = now;
        track.stop();
      }
    });
    this.currentTrack = null;
    this.currentTrackId = null;
  }

  setMusicVolume(volume) {
    this.musicVolume = volume;
    Object.values(this.tracks).forEach(track => {
      if (track.playing()) {
        track.volume(volume);
      }
    });
  }

  // Utility methods
  clearPlaybackHistory() {
    this.lastStopPositions = {};
    this.lastStopTimes = {};
  }

  getCurrentPosition() {
    if (this.currentTrack && this.currentTrackId) {
      return this.currentTrack.seek();
    }
    return null;
  }

  setFadeTime(milliseconds) {
    this.fadeTime = milliseconds;
  }

  setReplayThreshold(milliseconds) {
    this.replayThreshold = milliseconds;
  }

  isPlaying(trackId) {
    return this.tracks[trackId]?.playing() || false;
  }

  getCurrentTrackId() {
    return this.currentTrackId;
  }
}

class GameAudioManager {
  constructor() {
    this.sounds = {};
    this.musicManager = new MusicManager();
    this.effectsVolume = 0.5;
    this.manifest = null;
    this.isMuted = false;
    this.savedVolumes = {
      master: 1,
      effects: 0.5,
      music: 0.5
    };

    this.maxConcurrentSounds = 32;
    this.maxPerSoundId = 4;
    this.priorityLevels = {
      CRITICAL: 1000,
      HIGH: 100,
      MEDIUM: 50,
      LOW: 10
    };
    this.activeInstances = new Map();
    this.soundCategories = new Map();
  }

  setMute(muted) {
    if (muted === this.isMuted) return;

    if (muted) {
      // Save current volumes
      this.savedVolumes = {
        master: Howler.volume(),
        effects: this.effectsVolume,
        music: this.musicManager.musicVolume
      };
      // Set all volumes to 0
      this.setMasterVolume(0);
      this.setEffectsVolume(0);
      this.setMusicVolume(0);
    } else {
      // Restore saved volumes
      this.setMasterVolume(this.savedVolumes.master);
      this.setEffectsVolume(this.savedVolumes.effects);
      this.setMusicVolume(this.savedVolumes.music);
    }
    this.isMuted = muted;
  }

  load() {
    this.loadManifest(SOUNDS);
  }

  loadManifest(manifest) {
    this.manifest = manifest;

    if (manifest.soundCategories) {
      Object.entries(manifest.soundCategories).forEach(([category, sounds]) => {
        this.soundCategories.set(category, new Set(sounds));
      });
    }

    this.initFromManifest();
  }

  initFromManifest() {
    if (this.manifest.music) {
      Object.entries(this.manifest.music).forEach(([trackId, config]) => {
        this.musicManager.addTrack(trackId, config);
      });
    }

    if (this.manifest.sfx) {
      Object.entries(this.manifest.sfx).forEach(([soundId, config]) => {
        console.log(config);
        this.sounds[soundId] = new Howl({
          src: [config.src || config.srcs],
          loop: config.loop ?? false,
          volume: (config.volume || 1) * this.effectsVolume
        });
      });
    }
  }

  playMusic(trackId) {
    this.musicManager.playMusic(trackId);
  }

  playSound(soundId, priority = this.priorityLevels.MEDIUM) {
    if (!this.sounds[soundId]) {
      console.warn(`Sound effect ${soundId} not found`);
      return null;
    }

    const currentInstances = this.activeInstances.get(soundId) || new Set();

    if (currentInstances.size >= this.maxPerSoundId) {
      this.handleSoundLimit(soundId, priority);
      return null;
    }

    const totalActiveSounds = Array.from(this.activeInstances.values())
      .reduce((total, instances) => total + instances.size, 0);

    if (totalActiveSounds >= this.maxConcurrentSounds) {
      this.handleTotalSoundLimit(priority);
    }

    const sound = this.sounds[soundId].play();
    if (!this.activeInstances.has(soundId)) {
      this.activeInstances.set(soundId, new Set());
    }
    this.activeInstances.get(soundId).add(sound);

    this.sounds[soundId].once('end', () => {
      this.activeInstances.get(soundId).delete(sound);
      if (this.activeInstances.get(soundId).size === 0) {
        this.activeInstances.delete(soundId);
      }
    }, sound);

    const baseVolume = this.manifest.sfx[soundId].volume ?? 1;
    const volumeScale = this.calculateVolumeScale(priority);
    this.sounds[soundId].volume(this.effectsVolume * baseVolume * volumeScale, sound);

    return sound;
  }

  handleSoundLimit(soundId, newPriority) {
    const instances = this.activeInstances.get(soundId);
    if (!instances) return;

    let lowestPriority = Infinity;
    let lowestPriorityId = null;

    instances.forEach(id => {
      const priority = this.sounds[soundId]._priorityMap?.get(id) || 0;
      if (priority < lowestPriority) {
        lowestPriority = priority;
        lowestPriorityId = id;
      }
    });

    if (lowestPriority < newPriority && lowestPriorityId) {
      this.sounds[soundId].stop(lowestPriorityId);
      instances.delete(lowestPriorityId);
    }
  }

  handleTotalSoundLimit(newPriority) {
    let lowestPriority = Infinity;
    let soundToStop = null;
    let instanceToStop = null;

    this.activeInstances.forEach((instances, soundId) => {
      instances.forEach(instance => {
        const priority = this.sounds[soundId]._priorityMap?.get(instance) || 0;
        if (priority < lowestPriority) {
          lowestPriority = priority;
          soundToStop = soundId;
          instanceToStop = instance;
        }
      });
    });

    if (lowestPriority < newPriority && soundToStop && instanceToStop) {
      this.sounds[soundToStop].stop(instanceToStop);
      this.activeInstances.get(soundToStop).delete(instanceToStop);
      if (this.activeInstances.get(soundToStop).size === 0) {
        this.activeInstances.delete(soundToStop);
      }
    }
  }

  calculateVolumeScale(priority) {
    const totalSounds = Array.from(this.activeInstances.values())
      .reduce((total, instances) => total + instances.size, 0);

    const volumeScale = Math.max(0.4, 1 - (totalSounds / this.maxConcurrentSounds) * 0.6);
    const priorityBoost = priority / this.priorityLevels.MEDIUM;

    return Math.min(1, volumeScale * priorityBoost);
  }

  playCategorySound(category, count = 1) {
    const sounds = this.soundCategories.get(category);
    if (!sounds || sounds.size === 0) return;

    const soundArray = Array.from(sounds);
    for (let i = 0; i < count; i++) {
      const randomIndex = Math.floor(Math.random() * soundArray.length);
      this.playSound(soundArray[randomIndex], this.priorityLevels.LOW);
    }
  }

  stopAllMusic() {
    this.musicManager.stopAllMusic();
  }

  setMasterVolume(volume) {
    Howler.volume(volume);
  }

  setEffectsVolume(volume) {
    this.effectsVolume = volume;
    Object.entries(this.sounds).forEach(([soundId, sound]) => {
      const baseVolume = this.manifest.sfx[soundId].volume ?? 1;
      sound.volume(volume * baseVolume);
    });
  }

  setMusicVolume(volume) {
    this.musicManager.setMusicVolume(volume);
  }
}

export { GameAudioManager };